import * as React from 'react';
import * as calendarActions from '../photographerCalendars/actions/CalendarActions';
import * as Actions from './actions/CalendarActions';
import moment from 'moment';
import SuccessSnackbar from '../../shared/feedback/SuccessSnackbar';
import FailureSnackbar from '../../shared/feedback/FailureSnackbar';
import { Translate } from '../../shared/LanguageService';
import { connect } from 'react-redux';
import { assign } from '../../shared/Assign';
import { IPhotographerCalendar } from './Types';
import ICalendar from '../calendar/ICalendar';
import PhotographerCalendarComponent from './PhotographerCalendarComponent';
import { useMediaQuery, useTheme } from '@mui/material';

interface IActions {
    getCalendar: (from: any, to: any) => any;
    updateCalendar: (calendar: any) => any;
    onCreated: (event: any) => Promise<any>;
    onUpdated: (event: any) => Promise<any>;
    onDeleted: (event: any) => Promise<any>;
}

interface IProps {
    calendar: ICalendar;
    actions: IActions;
    user: any;
}

function PhotographerCalendar(props: IProps) {
    const theme = useTheme();
    const isSmallerScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [calendar, setCalendar] = React.useState<IPhotographerCalendar>({
        items: [],
        workingHours: [],
        se360WorkingHours: [],
        scandphotosWorkingHours: [],
        userIds: []
    });

    const [successText, setSuccessText] = React.useState<string | null>(null);
    const [failureText, setFailureText] = React.useState<string | null>(null);

    const [receivedCalendar, setReceivedCalendar] = React.useState(props.calendar);

    if (props.calendar !== receivedCalendar) {
        setReceivedCalendar(props.calendar);
        setCalendar(assign({}, props.calendar));
    }

    function onCreated(event: any) {
        props.actions.onCreated(event).then(() => {
            setSuccessText(Translate('calendars/photographer-items/update/toastr/success'));
        });
    }

    function onDeleted(event: any) {
        props.actions.onDeleted(event).then(() => {
            setSuccessText(Translate('calendars/photographer-items/update/toastr/success'));
        });
    }

    function onUpdated(event: any) {
        props.actions.onUpdated(event).then(() => {
            setSuccessText(Translate('calendars/photographer-items/update/toastr/success'));
        });
    }

    function onCalendarChange(changedCalendar: ICalendar) {
        setCalendar(assign({}, changedCalendar));
    }

    function updateCalendar() {
        let calendarToUpdate = {
            items: calendar.items.map((item: any) => {
                return {
                    id: item.id,
                    userId: item.userId,
                    orderId: item.orderId,
                    title: item.title,
                    description: item.description,
                    type: item.type,
                    start: moment(item.start).format(),
                    end: moment(item.end).format(),
                    className: item.className,
                    site: item.site,
                    PhotographerInformation: item.PhotographerInformation,
                    FirmName: item.FirmName,
                    BrokerName: item.BrokerName,
                    BrokerDeliveryEmail: item.BrokerDeliveryEmail,
                    BrokerMobileNumber: item.BrokerMobileNumber
                };
            }),
            se360WorkingHours: calendar.se360WorkingHours,
            scandphotosWorkingHours: calendar.scandphotosWorkingHours
        };

        calendarToUpdate.se360WorkingHours.forEach((x) => {
            x.start = moment(moment(x.start).toDate()).format('HH:mm:ss');
            x.end = moment(moment(x.end).toDate()).format('HH:mm:ss');
        });

        calendarToUpdate.scandphotosWorkingHours.forEach((x) => {
            x.start = moment(moment(x.start).toDate()).format('HH:mm:ss');
            x.end = moment(moment(x.end).toDate()).format('HH:mm:ss');
        });

        props.actions
            .updateCalendar({ calendar: calendarToUpdate })
            .then(() => {
                setSuccessText(Translate('photographers/calendar/toastr/success'));
            })
            .catch(() => {
                setFailureText(Translate('photographers/calendar/toastr/error'));
            });
    }

    return (
        <>
            <PhotographerCalendarComponent
                onSave={() => {
                    updateCalendar();
                }}
                calendar={calendar}
                onChange={(changedCalendar: ICalendar) => {
                    onCalendarChange(changedCalendar);
                }}
                onCreated={onCreated}
                onUpdated={onUpdated}
                onDeleted={onDeleted}
                onRangeChanged={(from: any, to: any) => {
                    props.actions.getCalendar(from, to);
                }}
                user={props.user}
                calendarSettings={{
                    id: 'photographer-personal-calendar',
                    defaultView: isSmallerScreen ? 'agendaDay' : 'agendaWeek',
                    header: {
                        left: 'prev,next',
                        center: 'title',
                        right: 'agendaDay,agendaWeek,month'
                    },
                    nowIndicator: true
                }}
            />
            <SuccessSnackbar
                open={successText !== null}
                onClose={() => setSuccessText(null)}
                text={successText || ''}
            />
            <FailureSnackbar
                open={failureText !== null}
                onClose={() => setFailureText(null)}
                text={failureText || ''}
            />
        </>
    );
}

function mapStateToProps(state: any) {
    return {
        calendar: state.calendar,
        user: state.user
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            getCalendar: (from: any, to: any) => dispatch(Actions.getCalendar(from, to)),
            updateCalendar: (calendar: any) => dispatch(Actions.updateCalendar(calendar)),
            onCreated: (item: any) =>
                dispatch(calendarActions.createPhotographerCalendarItem(item)),
            onDeleted: (item: any) =>
                dispatch(calendarActions.deletePhotographerCalendarItem(item)),
            onUpdated: (item: any) => dispatch(calendarActions.updatePhotographerCalendarItem(item))
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(PhotographerCalendar);
